<template>
  <template v-if="$route.name === 'guardians.students'">
    <div class="col-12 col-xl-12">
      <!-- Header actions -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <router-link
          v-if="(isSchoolAdmin || isStudentAffairs) && !isGuardian"
          :to="{ name: 'guardians.students.add', params: { id: route.params.id } }"
          class="btn btn-secondary btn-sm"
          v-can="'student_guardians.create'"
        >
          <i class="fas fa-plus me-2"></i>
          {{ $t('students.addBtn') }}
        </router-link>
      </div>

      <!-- Card -->
      <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <h6 v-if="guardian" class="text-white text-capitalize ps-3 pe-3">
              {{ isGuardian ? $t('students.your_students') : $t('students.guardian_students', { name: guardian.username }) }}
            </h6>
            <h6 v-else class="text-white text-capitalize ps-3 pe-3">
              {{ $t('students.loading_guardian') }}
            </h6>
          </div>
        </div>

        <div class="card-body px-0 pb-2">
          <div class="table-responsive p-2 p-md-4">
            <LoadingSpinner v-if="isLoading" :message="$t('students.loading_students')" />

            <template v-else-if="guardian">
              <!-- Mobile list -->
              <div class="d-block d-xl-none">
                <div v-if="students.length === 0" class="text-center py-4 text-muted">
                  <i class="fas fa-users-slash fa-2x mb-2"></i>
                  <p>{{ $t('students.no_students') }}</p>
                </div>

                <div v-for="student in students" :key="student.id" class="mb-4 border-bottom pb-3 m-2">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0 text-md">{{ formatStudentName(student) }}</h6>

                    <div class="d-flex align-items-center gap-2">
                      <!-- Dashboard badge -->
                      <span
                        class="badge badge-sm"
                        :class="student.pivot?.use_dashboard ? 'bg-success' : 'bg-secondary'"
                        :title="student.pivot?.use_dashboard ? $t('students.dashboard_used') : $t('students.dashboard_not_used')"
                      >
                        {{ student.pivot?.use_dashboard ? $t('students.dashboard_used') : $t('students.dashboard_not_used') }}
                      </span>

                      <!-- Bus badge -->
                      <span
                        class="badge badge-sm"
                        :class="isOnBus(student) ? 'bg-success' : 'bg-secondary'"
                        :title="busTooltip(student)"
                      >
                        <i class="fas fa-bus me-1"></i>
                        {{ isOnBus(student) ? ($t('buses.onBus') || 'On bus') : ($t('buses.noBus') || 'No bus') }}
                      </span>
                    </div>
                  </div>

                  <div class="d-flex justify-content-between mb-1">
                    <small class="text-muted">{{ $t('students.birth_date') }}</small>
                    <p class="mb-0 text-xs font-weight-bold">{{ formatDate(student.birthdate) }}</p>
                  </div>

                  <div class="d-flex justify-content-between mb-1">
                    <small class="text-muted">{{ $t('students.relation') }}</small>
                    <p class="mb-0 text-xs font-weight-bold">{{ $t(`relations.${student.pivot?.relation_type || 'other'}`) }}</p>
                  </div>

                  <div class="d-flex justify-content-end mt-2 align-items-center gap-2 flex-wrap">
                    <!-- Wallet button -->
                    <router-link
                      v-if="isGuardian && showPaymentInfo"
                      :to="{ name: 'guardians.students.charge-wallet', params: { id: guardianId, studentId: student.id } }"
                      class="btn btn-link text-primary p-1 mx-1"
                      :title="$t('students.charge_wallet')"
                    >
                      <i class="fas fa-wallet"></i>
                    </router-link>

                    <!-- Activate/deactivate dashboard -->
                    <template v-if="isSchoolAdmin || isFinance">
                      <button
                        v-if="!student.pivot?.use_dashboard"
                        class="btn btn-sm btn-outline-primary"
                        :disabled="isActivating[student.id]"
                        @click="onActivateDashboard(student)"
                        :title="$t('students.use_dashboard_title')"
                      >
                        <span v-if="isActivating[student.id]" class="fas fa-spinner fa-spin"></span>
                        <span v-else>
                          <i class="fas fa-bolt"></i>
                          {{ $t('students.use_dashboard_btn', { fee: dashboardFeeLabel }) }}
                        </span>
                      </button>

                      <button
                        v-else
                        class="btn btn-sm btn-outline-danger"
                        :disabled="isActivating[student.id]"
                        @click="onDeactivateDashboard(student)"
                        :title="$t('students.deactivate_dashboard_title')"
                      >
                        <span v-if="isActivating[student.id]" class="fas fa-spinner fa-spin"></span>
                        <span v-else>
                          <i class="fas fa-undo"></i>
                          {{ $t('students.deactivate_dashboard_btn') }}
                        </span>
                      </button>
                    </template>

                    <template v-if="!isGuardian">
                      <button
                        @click="openEditDialog(student)"
                        class="btn btn-link text-secondary p-1 mx-1"
                        :title="$t('students.edit_relation')"
                        v-can="'student_guardians.update'"
                      >
                        <i class="fas fa-edit fa-sm"></i>
                      </button>

                      <button
                        @click="openDeleteDialog(student)"
                        class="btn btn-link text-danger p-1 mx-1"
                        :title="$t('students.remove_relation')"
                        :disabled="isProcessing"
                        v-can="'student_guardians.delete'"
                      >
                        <i class="fas fa-trash fa-sm"></i>
                      </button>
                    </template>
                  </div>
                </div>
              </div>

              <!-- Desktop table -->
              <div class="d-none d-xl-block">
                <table class="table align-items-center mb-0" style="width: 100%">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7" style="width: 24%">
                        {{ $t('students.name') }}
                      </th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 10%">
                        {{ $t('students.birth_date') }}
                      </th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 8%">
                        {{ $t('students.gender') }}
                      </th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 12%">
                        {{ $t('students.relation') }}
                      </th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 22%">
                        {{ $t('students.dashboard_status') }}
                      </th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center" style="width: 10%">
                        {{ $t('buses.transport') || 'Transport' }}
                      </th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4" style="width: 14%">
                        {{ $t('students.actions') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="students.length === 0">
                      <td :colspan="6" class="text-center py-4 text-muted">
                        <i class="fas fa-users-slash fa-2x mb-2"></i>
                        <p>{{ $t('students.no_students') }}</p>
                      </td>
                    </tr>

                    <tr v-for="student in students" :key="student.id">
                      <td style="width: 24%">
                        <div class="d-flex px-3 py-1">
                          <div class="d-flex flex-column justify-content-center">
                            <h6 class="mb-0 text-md">{{ formatStudentName(student) }}</h6>
                          </div>
                        </div>
                      </td>

                      <td class="text-center" style="width: 10%">
                        <p class="text-xs font-weight-bold mb-0">{{ formatDate(student.birthdate) }}</p>
                      </td>

                      <td class="text-center" style="width: 8%">
                        <span class="badge badge-sm" :class="student.gender === 'M' ? 'bg-primary' : 'bg-secondary'">
                          {{ student.gender === 'M' ? $t('gender.male') : $t('gender.female') }}
                        </span>
                      </td>

                      <td class="text-center" style="width: 12%">
                        <p class="text-xs font-weight-bold mb-0">{{ $t(`relations.${student.pivot?.relation_type || 'other'}`) }}</p>
                      </td>

                      <td class="text-center" style="width: 22%">
                        <!-- Dashboard badge -->
                        <span class="badge me-2" :class="student.pivot?.use_dashboard ? 'bg-success' : 'bg-secondary'">
                          {{ student.pivot?.use_dashboard ? $t('students.dashboard_used') : $t('students.dashboard_not_used') }}
                        </span>

                        <!-- Activate/deactivate dashboard buttons -->
                        <div class="d-flex align-items-center justify-content-center gap-2 mt-2" v-if="isSchoolAdmin || isFinance">
                          <button
                            v-if="!student.pivot?.use_dashboard"
                            class="btn btn-sm btn-outline-primary"
                            :disabled="isActivating[student.id]"
                            @click="onActivateDashboard(student)"
                            :title="$t('students.use_dashboard_title')"
                          >
                            <span v-if="isActivating[student.id]" class="fas fa-spinner fa-spin"></span>
                            <span v-else>
                              <i class="fas fa-bolt"></i>
                              {{ $t('students.use_dashboard_btn', { fee: dashboardFeeLabel }) }}
                            </span>
                          </button>

                          <button
                            v-else
                            class="btn btn-sm btn-outline-danger"
                            :disabled="isActivating[student.id]"
                            @click="onDeactivateDashboard(student)"
                            :title="$t('students.deactivate_dashboard_title')"
                          >
                            <span v-if="isActivating[student.id]" class="fas fa-spinner fa-spin"></span>
                            <span v-else>
                              <i class="fas fa-undo"></i>
                              {{ $t('students.deactivate_dashboard_btn') }}
                            </span>
                          </button>
                        </div>
                      </td>

                      <td class="text-center" style="width: 10%">
                        <!-- Bus badge -->
                        <span class="badge" :class="isOnBus(student) ? 'bg-success' : 'bg-secondary'" :title="busTooltip(student)">
                          <i class="fas fa-bus me-1"></i>
                          {{ isOnBus(student) ? ($t('buses.onBus') || 'On bus') : ($t('buses.noBus') || 'No bus') }}
                        </span>
                      </td>

                      <td class="align-middle text-center pe-4" style="width: 14%">
                        <div class="d-inline-flex justify-content-center">
                          <!-- (No bus subscription action here) -->

                          <router-link
                            v-if="isGuardian && showPaymentInfo"
                            :to="{ name: 'guardians.students.charge-wallet', params: { id: guardianId, studentId: student.id } }"
                            class="btn btn-link text-primary p-1 mx-1"
                            :title="$t('students.charge_wallet')"
                          >
                            <i class="fas fa-wallet"></i>
                          </router-link>

                          <template v-if="!isGuardian">
                            <button
                              @click="openEditDialog(student)"
                              class="btn btn-link text-secondary p-1 mx-1"
                              :title="$t('students.edit_relation')"
                              v-can="'student_guardians.update'"
                            >
                              <i class="fas fa-edit fa-sm"></i>
                            </button>
                            <button
                              @click="openDeleteDialog(student)"
                              class="btn btn-link text-danger p-1 mx-1"
                              :title="$t('students.remove_relation')"
                              v-can="'student_guardians.delete'"
                              :disabled="isProcessing"
                            >
                              <i class="fas fa-trash fa-sm"></i>
                            </button>
                          </template>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </div>
      </div>

      <EditRelationDialog v-if="!isGuardian" ref="editDialog" @update="handleUpdateRelation" />
      <DeleteDialog v-if="!isGuardian" ref="confirmDialog" title="" @confirm="handleRemoveConfirm" />
    </div>
  </template>

  <router-view />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import EditRelationDialog from '@/components/features/studentGuardians/dialogs/EditRelationDialog.vue'
import DeleteDialog from '@/components/features/studentGuardians/dialogs/DeleteStudentGuardianDialog.vue'
import api from '@/api'

const { locale, t } = useI18n()
const store = useStore()
const route = useRoute()
const router = useRouter()
const toast = useToast()

const editDialog = ref(null)
const confirmDialog = ref(null)
const selectedStudentId = ref(null)

const isLoading = ref(false)
const isProcessing = ref(false)
const isActivating = ref({})

const dashboardFee = ref(0)
const dashboardFeeLabel = computed(() => `${Number(dashboardFee.value || 0)} ${t('guardians.currency')}`)

const currentUser = computed(() => store.state.user)
const isSchoolAdmin = computed(() => currentUser.value?.role?.slug === 'school_admin')
const isStudentAffairs = computed(() => currentUser.value?.role?.slug === 'student_affairs')
const isFinance = computed(() => currentUser.value?.role?.slug === 'finance')
const isGuardian = computed(() => currentUser.value?.role?.slug === 'guardian')

const guardianId = computed(() => (isGuardian.value ? currentUser.value.id : route.params.id))
const guardian = computed(() => (isGuardian.value ? currentUser.value : store.getters['guardian/getCurrentGuardian']))
const students = computed(() => store.getters['studentGuardian/guardianStudents'] || [])
const showPaymentInfo = computed(() => isSchoolAdmin.value || isFinance.value || isGuardian.value)

const busIndex = ref({})

const isOnBus = (s) => {
  const x = busIndex.value?.[s.id]
  if (x) return !!x.active

  if (typeof s?.active_bus_subscription === 'boolean') return s.active_bus_subscription
  if (s?.bus_subscription_status) return s.bus_subscription_status === 'active'
  if (s?.bus_subscription) {
    const sub = s.bus_subscription
    if (typeof sub?.is_active === 'boolean') return sub.is_active
    if (sub?.status) return sub.status === 'active'
  }
  if (Array.isArray(s?.bus_subscriptions)) {
    return s.bus_subscriptions.some(x => x?.status === 'active' || x?.is_active === true)
  }
  if (s?.bus_area_id || s?.bus_id) return true
  return false
}

const busTooltip = (s) => {
  const x = busIndex.value?.[s.id]
  if (x) {
    if (!x.active) return t('buses.noBus') || 'No bus'
    if (x.bus_name && x.area_name) return `${x.bus_name} — ${x.area_name}`
    if (x.bus_name) return x.bus_name
    if (x.area_name) return x.area_name
    return t('buses.onBus') || 'On bus'
  }

  const busName =
    s?.bus?.name ||
    s?.bus_subscription?.bus?.name ||
    (Array.isArray(s?.bus_subscriptions) ? s.bus_subscriptions.find(y => y?.status === 'active')?.bus?.name : null) ||
    null

  const areaName =
    s?.bus_area?.area_name ||
    s?.bus_subscription?.bus_area?.area_name ||
    (Array.isArray(s?.bus_subscriptions) ? s.bus_subscriptions.find(y => y?.status === 'active')?.bus_area?.area_name : null) ||
    null

  if (!isOnBus(s)) return t('buses.noBus') || 'No bus'
  if (busName && areaName) return `${busName} — ${areaName}`
  if (busName) return busName
  if (areaName) return areaName
  return t('buses.onBus') || 'On bus'
}

const formatDate = (dateString) => {
  const opt = { year: 'numeric', month: 'long', day: 'numeric' }
  return new Date(dateString).toLocaleDateString(locale.value, opt)
}
const formatStudentName = (s) => `${s.first_name} ${s.father_name} ${s.last_name}`

const fetchData = async () => {
  isLoading.value = true
  try {
    if (!isGuardian.value) await store.dispatch('guardian/fetchGuardian', guardianId.value)
    await store.dispatch('studentGuardian/fetchGuardianStudents', guardianId.value)

    const ids = (students.value || []).map(s => s.id)
    if (ids.length) {
      const { data } = await api.get(`/guardians/${guardianId.value}/bus-subscriptions`, {
        params: { student_ids: ids }
      })
      busIndex.value = data?.data || {}
    } else {
      busIndex.value = {}
    }
  } catch (e) {
    toast.error(t('students.fetch_failed'))
  } finally {
    isLoading.value = false
  }
}

const onActivateDashboard = async (student) => {
  if (student.pivot?.use_dashboard) return
  isActivating.value = { ...isActivating.value, [student.id]: true }
  try {
    const { data } = await api.post(`/guardians/${guardianId.value}/students/${student.id}/activate-dashboard`)
    toast.success(t('students.dashboard_activated_toast', { name: formatStudentName(student) }))
    if (data?.guardian_activated) {
      toast.success(t('students.guardian_activated_toast', { guardian: guardian.value?.username || '' }))
    }
    await store.dispatch('studentGuardian/fetchGuardianStudents', guardianId.value)
    await fetchData() 
  } catch (error) {
    const status = error?.response?.status
    if (status === 409) {
      toast.info(t('students.dashboard_already_activated_toast', { name: formatStudentName(student) }))
    } else if (status === 422) {
      const required = error?.response?.data?.required ?? ''
      const available = error?.response?.data?.available ?? ''
      toast.error(t('students.insufficient_wallet_toast', { required, available }))
    } else {
      toast.error(error?.response?.data?.message || t('students.dashboard_activation_failed'))
    }
  } finally {
    isActivating.value = { ...isActivating.value, [student.id]: false }
  }
}

const onDeactivateDashboard = async (student) => {
  if (!student.pivot?.use_dashboard) return
  isActivating.value = { ...isActivating.value, [student.id]: true }
  try {
    await api.post(`/guardians/${guardianId.value}/students/${student.id}/deactivate-dashboard`)
    toast.success(t('students.dashboard_deactivated_toast', { name: formatStudentName(student) }))
    await store.dispatch('studentGuardian/fetchGuardianStudents', guardianId.value)
    await fetchData()
  } catch (error) {
    toast.error(error?.response?.data?.message || t('students.dashboard_deactivation_failed'))
  } finally {
    isActivating.value = { ...isActivating.value, [student.id]: false }
  }
}

const openDeleteDialog = (student) => {
  if (!guardian.value) return
  selectedStudentId.value = student.id
  confirmDialog.value.open(t('students.remove_relation_confirm', { guardian: guardian.value.username, student: student.first_name }))
}

const handleUpdateRelation = async ({ studentId, guardianId, relationType }) => {
  isProcessing.value = true
  try {
    await store.dispatch('studentGuardian/updateGuardianStudentRelation', { guardianId, studentId, relationType })
    await fetchData()
    toast.success(t('students.relation_updated'))
  } catch (error) {
    toast.error(error.response?.data?.message || t('students.relation_update_failed'))
  } finally {
    isProcessing.value = false
  }
}

const handleRemoveConfirm = async () => {
  if (!selectedStudentId.value) return
  try {
    await store.dispatch('studentGuardian/removeStudentFromGuardian', {
      guardianId: route.params.id,
      studentId: selectedStudentId.value
    })
    toast.success(t('students.relation_removed'))
    await fetchData()
  } catch (error) {
    toast.error(error.response?.data?.message || t('students.relation_remove_failed'))
  } finally {
    selectedStudentId.value = null
  }
}

onMounted(async () => {
  try {
    const s = await api.get('/financial-settings')
    const fee = s?.data?.student_account_fee ?? s?.data?.data?.student_account_fee ?? 50
    dashboardFee.value = Number(fee)
  } catch {
    dashboardFee.value = 50
  }

  if (isGuardian.value && route.params.id && route.params.id !== currentUser.value.id.toString()) {
    router.push({ name: 'guardians.students', params: { id: currentUser.value.id } })
    return
  }

  await fetchData()
})
</script>

<style scoped>
.btn-primary {
  background-color: #23819B;
}
</style>